// React / Next
import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';

// Utils
import { cn } from '../../../utils/cn';

// Types
type CTAButtonVariant = 'primary' | 'secondary' | 'dark';

type CTAButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  variant?: CTAButtonVariant;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  className?: string;
};

const variantStyles: Record<CTAButtonVariant, string> = {
  primary: cn(
    'bg-orange-800 text-white',
    'hover:bg-orange-1000',
    'focus:ring-orange-800'
  ),
  secondary: cn(
    'bg-orange-200 text-orange-1000',
    'hover:bg-orange-800 hover:text-white',
    'focus:ring-orange-200'
  ),
  dark: cn(
    'bg-blue-1000 text-white',
    'hover:bg-gray-1000',
    'focus:ring-blue-1000'
  ),
};

/**
 * Custom CTA Button with multiple variants
 * Based on Archie Design System button styles for the website
 *
 * @param variant - Button style variant:
 *   - 'primary' (default): orange-800 background, white text
 *   - 'secondary': orange-200 background, orange-1000 text
 *   - 'dark': blue-1000 background, white text
 */
export const CTAButton = forwardRef<HTMLButtonElement, CTAButtonProps>(
  ({ children, variant = 'primary', startIcon, endIcon, className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-[var(--spacing-8)]',
          'px-[var(--spacing-16)] py-[var(--spacing-12)]',
          'rounded-[var(--radius-8)]',
          'font-body text-[16px] leading-[24px] font-normal',
          'transition-colors duration-200',
          'focus:outline-none focus:ring-2 focus:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          variantStyles[variant],
          className
        )}
        {...props}
      >
        {startIcon && <span className="shrink-0">{startIcon}</span>}
        <span>{children}</span>
        {endIcon && <span className="shrink-0">{endIcon}</span>}
      </button>
    );
  }
);

CTAButton.displayName = 'CTAButton';

